import { useEffect, useState } from 'react'
import * as api from 'api/entityApi'
import { useToast } from 'components/common/Toast'
import type { Option } from 'types/entity'
import { ContactCheckboxes, useTriSelection, type EntityContact, EMPTY_CONTACT, type TriValues } from './ContactCheckboxes'

interface SmsFormProps {
  entityId: string
  entityType?: 'lead' | 'enquiry'
  contact?: EntityContact
  onSuccess?: (msg: string) => void
  onCancel?: () => void
}

/** Port of the legacy Send SMS action (CRMService/SendSms, DLT flow). */
export function SmsForm({ entityId, entityType = 'lead', contact = EMPTY_CONTACT, onSuccess }: SmsFormProps) {
  const toast = useToast()

  const [senderIds, setSenderIds] = useState<Option[]>([])
  const [templates, setTemplates] = useState<Option[]>([])

  const [senderId, setSenderId] = useState<string>('')
  const [templateId, setTemplateId] = useState<string>('')
  const [message, setMessage] = useState<string>('')
  const [isUnicode, setIsUnicode] = useState<boolean>(false)

  const mobileValues: TriValues = [contact.mobileNo, contact.mobileNo1, contact.mobileNo2]
  const mobiles = useTriSelection(mobileValues)

  const [loading, setLoading] = useState<boolean>(true)
  const [templatesLoading, setTemplatesLoading] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    let active = true
    async function loadData() {
      try {
        const senders = await api.fetchSmsSenderIds()
        if (active) setSenderIds(senders)
      } catch {
        if (active) setError('Failed to load SMS sender ids.')
      } finally {
        if (active) setLoading(false)
      }
    }
    void loadData()
    return () => {
      active = false
    }
  }, [])

  /** Legacy flow: sender change reloads the DLT template list. */
  const handleSenderChange = async (value: string) => {
    setSenderId(value)
    setTemplateId('')
    setMessage('')
    setIsUnicode(false)
    setTemplates([])
    if (!value) return
    setTemplatesLoading(true)
    try {
      setTemplates(await api.fetchSmsTemplates(value))
    } catch {
      setError('Failed to load SMS templates for this sender.')
    } finally {
      setTemplatesLoading(false)
    }
  }

  /** Template change pulls the approved DLT text into the message box. */
  const handleTemplateChange = async (value: string) => {
    setTemplateId(value)
    if (!value) {
      setMessage('')
      setIsUnicode(false)
      return
    }
    try {
      const details = await api.fetchDltTemplateDetails(value)
      setMessage(details.text)
      setIsUnicode(details.isUnicode)
    } catch {
      setError('Failed to load the template text.')
    }
  }

  const handleReset = () => {
    setSenderId('')
    setTemplateId('')
    setTemplates([])
    setMessage('')
    setIsUnicode(false)
    mobiles.reset()
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!senderId) {
      setError('Please select a sender id.')
      return
    }
    if (!templateId) {
      setError('Please select an SMS template.')
      return
    }
    if (!mobiles.anySelected) {
      setError('Please select at least one mobile number.')
      return
    }
    if (!message.trim()) {
      setError('SMS Message text is required.')
      return
    }
    setError('')
    setSubmitting(true)
    try {
      // Pre-send DLT compliance check, exactly as the legacy form ran it.
      const valid = await api.validateDltTemplate(templateId, message)
      if (!valid) {
        toast.push('error', 'Message does not match the approved DLT template.')
        return
      }
      const msg = await api.submitSms({
        entityId,
        entityType,
        senderId,
        templateId,
        mobileNo: mobiles.selected[0],
        mobileNo1: mobiles.selected[1],
        mobileNo2: mobiles.selected[2],
        message,
        isUnicode,
      })
      onSuccess?.(msg)
    } catch (err) {
      setError((err as Error).message || 'Failed to send SMS.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <div className="widget-loader">Loading SMS settings…</div>

  const charCount = message.length
  const perSms = isUnicode ? 70 : 160
  const smsCount = Math.ceil(charCount / perSms) || 1

  return (
    <div className="action-form">
      {error && <div className="alert alert-error">{error}</div>}

      <div className="form-group">
        <label className="form-label" htmlFor="sms-sender">
          Sender ID <span className="req">*</span>
        </label>
        <select
          id="sms-sender"
          className="form-control"
          value={senderId}
          onChange={(e) => void handleSenderChange(e.target.value)}
        >
          <option value="">-- Select Sender ID --</option>
          {senderIds.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="sms-template">
          SMS Template <span className="req">*</span>
        </label>
        <select
          id="sms-template"
          className="form-control"
          value={templateId}
          disabled={!senderId || templatesLoading}
          onChange={(e) => void handleTemplateChange(e.target.value)}
        >
          <option value="">{templatesLoading ? 'Loading templates…' : '-- Select Template --'}</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      <ContactCheckboxes
        label="Mobile Number(s)"
        idPrefix="sms-mobile"
        values={mobileValues}
        checked={mobiles.checked}
        onToggle={mobiles.toggle}
        emptyMessage="This record has no mobile number."
      />

      <div className="form-group">
        <label className="form-label" htmlFor="sms-message">
          Message <span className="req">*</span>{' '}
          {isUnicode && <span className="tag-chip">Unicode</span>}
        </label>
        <textarea
          id="sms-message"
          rows={4}
          className="form-control"
          placeholder="Type message here..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <div className="form-hint">
          Chars: {charCount} | SMS Credits: {smsCount}
        </div>
      </div>

      <div className="action-form-footer">
        <button type="button" className="btn btn-default" onClick={handleReset} disabled={submitting}>
          Reset
        </button>
        <button type="button" className="btn pri-btn" disabled={submitting} onClick={handleSubmit}>
          {submitting ? 'Sending…' : 'Send SMS'}
        </button>
      </div>
    </div>
  )
}
